'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import { motion } from 'motion/react';
import { Lightbulb, Star, Users, MapPin, TrendingUp, Handshake, ChevronDown } from 'lucide-react';
import { LazyVideo } from './LazyVideo';
import svgPaths from './imports/svg-gt5cjxv9eu';

import dynamic from 'next/dynamic';
const AnimatedBlobBackground = dynamic(
  () => import('./AnimatedBlobBackground').then((mod) => mod.AnimatedBlobBackground),
  { ssr: false },
);

export function ResponsiveAboutUs() {
  const router = useRouter();

  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkSize = () => {
      setShowVideo(true);
      setIsMobile(window.innerWidth < 640);
    };
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);
  const profileImages = [
    '/images/ProfilePic1.webp',
    '/images/ProfilePic2.webp',
    '/images/ProfilePic3.webp',
    '/images/ProfilePic4.webp',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % profileImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const spinSlow = {
    animate: {
      rotate: 360,
      transition: {
        duration: 8,
        repeat: Infinity,
        ease: 'linear' as const,
      },
    },
  };

  const pulseBorderBlue = {
    animate: {
      opacity: [0.4, 0.7, 0.4],
      boxShadow: [
        '0 0 20px rgba(34, 211, 238, 0.3), 0 0 40px rgba(34, 211, 238, 0.2)',
        '0 0 30px rgba(34, 211, 238, 0.5), 0 0 60px rgba(34, 211, 238, 0.3)',
        '0 0 20px rgba(34, 211, 238, 0.3), 0 0 40px rgba(34, 211, 238, 0.2)',
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut' as const,
      },
    },
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
      {/* Animated Blob Background with scroll-based colors */}
      <AnimatedBlobBackground enableScrollColors={true} blobCount={8} />

      <div className="relative z-10">
        <ResponsiveHeader />

        {/* Hero Section - Wolfgang & Cards */}
        <section className="relative px-4 sm:px-6 lg:px-8 py-12 lg:py-20 xl:py-24 overflow-hidden">
          {/* Animated Background Blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Purple Gradient Blob - Top Left */}
            <motion.div
              className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, rgba(147, 51, 234, 0.4) 0%, rgba(147, 51, 234, 0.2) 50%, transparent 100%)',
              }}
              animate={{
                x: [0, 50, 0],
                y: [0, 30, 0],
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            {/* Pink Gradient Blob - Top Right */}
            <motion.div
              className="absolute top-20 right-0 w-[600px] h-[600px] rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, rgba(236, 72, 153, 0.35) 0%, rgba(236, 72, 153, 0.15) 50%, transparent 100%)',
              }}
              animate={{
                x: [0, -60, 0],
                y: [0, 40, 0],
                scale: [1, 1.3, 1],
                opacity: [0.25, 0.45, 0.25],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1,
              }}
            />

            {/* Cyan Gradient Blob - Bottom Center */}
            <motion.div
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, rgba(34, 211, 238, 0.3) 0%, rgba(34, 211, 238, 0.1) 50%, transparent 100%)',
              }}
              animate={{
                x: [-50, 50, -50],
                y: [0, -30, 0],
                scale: [1, 1.15, 1],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2,
              }}
            />

            {/* Gradient Overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#18181b]/50 via-transparent to-[#18181b]/30" />
          </div>

          <div className="max-w-7xl mx-auto pt-8 sm:pt-12 lg:pt-16 relative z-10">
            {/* Title */}
            <motion.div
              className="text-center mb-8 lg:mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-2 px-[0px] py-[25px] font-bold font-normal">
                Wolfgang Sakamaki{' '}
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent font-bold">
                  Zumify
                </span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl text-white/90 mt-2">
                  Meet the Founder
                </span>
              </h1>
            </motion.div>

            {/* Two Column Layout: Cards Left, Wolfgang Right */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 xl:gap-12">
              {/* Left Side - Two Cards Stacked */}
              <motion.div
                className="w-full max-w-md lg:max-w-[420px] flex flex-col gap-6 lg:gap-8 order-2 lg:order-1"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
              >
                {/* Programs I Use Card */}
                <motion.div
                  className="group relative backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-[45px] p-6 lg:p-8 border-2 border-purple-500/30 transition-all duration-500 hover:border-cyan-400/60 hover:shadow-2xl hover:shadow-cyan-500/30"
                  whileHover={{ y: -5, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Animated gradient background on hover */}
                  <div className="absolute inset-0 rounded-[45px] bg-gradient-to-br from-purple-600/0 via-pink-600/0 to-cyan-600/0 group-hover:from-purple-600/10 group-hover:via-pink-600/10 group-hover:to-cyan-600/10 transition-all duration-500 pointer-events-none" />

                  <div className="relative z-10">
                    <h3 className="text-2xl lg:text-3xl text-white text-center mb-4 lg:mb-6 group-hover:text-cyan-300 transition-colors duration-300">
                      Programs I Use
                    </h3>
                    <div className="flex justify-center items-center mb-4 h-16 lg:h-20 w-full px-4">
                      <img
                        src="/images/design-logos.png"
                        alt="Design Programs Figma Canva AI"
                        className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-center text-white/80 text-sm lg:text-base group-hover:text-white transition-colors duration-300">
                      Figma, Canva, Adobe InDesign, Antigravity
                    </p>
                  </div>

                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-[45px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1/2 bg-cyan-400/20 blur-2xl" />
                  </div>
                </motion.div>

                {/* Supported Platforms Card */}
                <motion.div
                  className="group relative backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-[45px] p-6 lg:p-8 border-2 border-purple-500/30 transition-all duration-500 hover:border-cyan-400/60 hover:shadow-2xl hover:shadow-cyan-500/30"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.1, ease: 'easeOut', delay: 0.3 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  {/* Animated gradient background on hover */}
                  <div className="absolute inset-0 rounded-[45px] bg-gradient-to-br from-cyan-600/0 via-purple-600/0 to-pink-600/0 group-hover:from-cyan-600/10 group-hover:via-purple-600/10 group-hover:to-pink-600/10 transition-all duration-500 pointer-events-none" />

                  <div className="relative z-10">
                    <h3 className="text-2xl lg:text-3xl text-white text-center mb-4 lg:mb-6 group-hover:text-cyan-300 transition-colors duration-300">
                      Platform Support
                    </h3>
                    <div className="flex flex-wrap justify-center items-center gap-4 mb-4 px-2">
                      <img src="/images/shopifyLogo.webp" alt="Shopify" className="h-8 lg:h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                      <img src="/images/squarespaceLogo.webp" alt="Squarespace" className="h-8 lg:h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                      <img src="/images/wixLogo.webp" alt="Wix" className="h-8 lg:h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                      <img src="/images/wordpressLogo.webp" alt="WordPress" className="h-8 lg:h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    </div>
                    <p className="text-center text-white/80 text-sm lg:text-base group-hover:text-white transition-colors duration-300">
                      We redesign and support current platforms
                    </p>
                  </div>

                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-[45px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-1/2 bg-cyan-400/20 blur-2xl" />
                  </div>
                </motion.div>

                {/* About Me Card */}
                <motion.div
                  className="group relative backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-[45px] p-6 lg:p-8 border-2 border-purple-500/30 transition-all duration-500 hover:border-pink-400/60 hover:shadow-2xl hover:shadow-pink-500/30"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut', delay: 0.4 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  {/* Animated gradient background on hover */}
                  <div className="absolute inset-0 rounded-[45px] bg-gradient-to-br from-pink-600/0 via-purple-600/0 to-cyan-600/0 group-hover:from-pink-600/10 group-hover:via-purple-600/10 group-hover:to-cyan-600/10 transition-all duration-500 pointer-events-none" />

                  <div className="relative z-10">
                    <h3 className="text-2xl lg:text-3xl text-white mb-4 lg:mb-6 group-hover:text-pink-300 transition-colors duration-300">
                      About me:
                    </h3>
                    <div className="space-y-2 text-white/80 text-lg lg:text-xl xl:text-2xl">
                      <motion.p
                        className="group-hover:text-white group-hover:translate-x-2 transition-all duration-300"
                        whileHover={{ x: 5 }}
                      >
                        HJJCC Director at Large
                      </motion.p>
                      <motion.p
                        className="group-hover:text-white group-hover:translate-x-2 transition-all duration-300"
                        whileHover={{ x: 5 }}
                      >
                        XC Assistant Coach
                      </motion.p>
                      <motion.p
                        className="group-hover:text-white group-hover:translate-x-2 transition-all duration-300"
                        whileHover={{ x: 5 }}
                      >
                        Contestant Squid Games S2
                      </motion.p>
                      <motion.p
                        className="group-hover:text-white group-hover:translate-x-2 transition-all duration-300"
                        whileHover={{ x: 5 }}
                      >
                        Surfer/Hiker
                      </motion.p>
                    </div>
                  </div>

                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-[45px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-1/2 bg-pink-400/20 blur-2xl" />
                  </div>
                </motion.div>
              </motion.div>

              {/* Right Side - Wolfgang Profile Card */}
              <motion.div
                className="relative w-full max-w-md lg:max-w-[450px] xl:max-w-[522px] order-1 lg:order-2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}
              >
                <motion.div
                  className="relative rounded-[108px] overflow-hidden shadow-2xl shadow-purple-500/40"
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  {/* Animated border gradient */}
                  <motion.div
                    className="absolute inset-0 rounded-[108px] p-[3px] bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-500 will-change-transform"
                    variants={spinSlow}
                    animate="animate"
                  >
                    <div className="w-full h-full rounded-[105px] bg-[#18181b]" />
                  </motion.div>

                  {/* Profile image */}
                  <div className="relative">
                    {/* Gradient border wrapper */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-500 p-[4px] rounded-[108px]">
                      <div className="w-full h-full rounded-[104px] bg-zinc-900" />
                    </div>

                    {/* Spacer to maintain layout dimensions based on image aspect ratio */}
                    <img
                      src={profileImages[0]}
                      alt="Spacer"
                      className="w-full h-auto object-cover relative z-0 rounded-[108px] opacity-0 pointer-events-none"
                      loading="eager"
                      fetchPriority="high"
                    />
                    
                    {profileImages.map((src, index) => (
                      <motion.img
                        key={src}
                        src={src}
                        alt={`Wolfgang - Profile ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover z-10 rounded-[108px]"
                        initial={{ opacity: index === 0 ? 1 : 0 }}
                        animate={{ opacity: currentImageIndex === index ? 1 : 0 }}
                        transition={{ duration: 1 }}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        fetchPriority={index === 0 ? 'high' : 'low'}
                      />
                    ))}

                    {/* Hover glow overlay */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-t from-purple-600/30 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 z-20 rounded-[108px]"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                    />
                  </div>

                  {/* Outer glow effect */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-cyan-500/20 rounded-[120px] blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Experience Statistics */}
        <section className="relative px-4 sm:px-6 lg:px-8 py-12 lg:py-16 overflow-hidden">
          {/* Hologram Container with Opening Animation */}
          <motion.div
            className="relative max-w-6xl mx-auto z-10"
            initial={{ scaleY: 0, opacity: 0 }}
            whileInView={{ scaleY: 1, opacity: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
            style={{ transformOrigin: 'center' }}
          >
            {/* Hologram Border Frame - Top */}
            <motion.div
              className="absolute top-0 left-0 right-0 h-[2px] sm:h-[3px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
            />

            {/* Hologram Border Frame - Bottom */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px] sm:h-[3px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
            />

            {/* Hologram Border Frame - Left */}
            <motion.div
              className="absolute top-0 bottom-0 left-0 w-[2px] sm:w-[3px] bg-gradient-to-b from-transparent via-blue-400 to-transparent"
              initial={{ scaleY: 0, opacity: 0 }}
              whileInView={{ scaleY: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
            />

            {/* Hologram Border Frame - Right */}
            <motion.div
              className="absolute top-0 bottom-0 right-0 w-[2px] sm:w-[3px] bg-gradient-to-b from-transparent via-blue-400 to-transparent"
              initial={{ scaleY: 0, opacity: 0 }}
              whileInView={{ scaleY: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
            />

            {/* Pulsing Outer Glow */}
            <motion.div
              className="absolute inset-0 rounded-2xl border-2 border-cyan-400/40 will-change-[box-shadow,opacity]"
              variants={pulseBorderBlue}
              animate="animate"
              style={{ animationDelay: '0.5s' }}
            />

            {/* Scanline Effect */}
            <motion.div
              className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <motion.div
                className="absolute inset-x-0 h-[100px] sm:h-[150px] bg-gradient-to-b from-cyan-400/20 via-cyan-400/5 to-transparent"
                animate={{
                  y: ['-100%', '200%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                  delay: 0.6,
                }}
              />
            </motion.div>

            {/* Hologram Blue Glow */}
            <div className="absolute inset-0 bg-cyan-500/5 rounded-2xl pointer-events-none" />
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 blur-xl rounded-2xl pointer-events-none" />

            {/* Content Container */}
            <motion.div
              className="relative bg-gradient-to-br from-zinc-900/80 via-zinc-800/60 to-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-12 border border-cyan-500/30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12">
                {/* Design Experience */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <div className="relative inline-block mb-4">
                    {/* Blurred background gradient number */}
                    <div
                      className="absolute inset-0 flex items-center justify-center text-6xl lg:text-7xl xl:text-8xl blur-[7.5px] opacity-50"
                      style={{
                        backgroundImage:
                          'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      3+
                    </div>
                    {/* Main gradient number */}
                    <div
                      className="relative text-6xl lg:text-7xl xl:text-8xl"
                      style={{
                        backgroundImage:
                          'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      3+
                    </div>
                  </div>
                  <p className="text-lg lg:text-2xl xl:text-3xl text-[#9ca3af]">
                    Years of Design
                    <br />
                    Experience
                  </p>
                </motion.div>

                {/* Business Management */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <div className="relative inline-block mb-4">
                    {/* Blurred background gradient number */}
                    <div
                      className="absolute inset-0 flex items-center justify-center text-6xl lg:text-7xl xl:text-8xl blur-[7.5px] opacity-50"
                      style={{
                        backgroundImage:
                          'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      8+
                    </div>
                    {/* Main gradient number */}
                    <div
                      className="relative text-6xl lg:text-7xl xl:text-8xl"
                      style={{
                        backgroundImage:
                          'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      8+
                    </div>
                  </div>
                  <p className="text-lg lg:text-2xl xl:text-3xl text-[#9ca3af]">
                    Years of Business
                    <br />
                    Management
                  </p>
                </motion.div>

                {/* Projects Worked On */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <div className="relative inline-block mb-4">
                    {/* Blurred background gradient number */}
                    <div
                      className="absolute inset-0 flex items-center justify-center text-6xl lg:text-7xl xl:text-8xl blur-[7.5px] opacity-50"
                      style={{
                        backgroundImage:
                          'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      13+
                    </div>
                    {/* Main gradient number */}
                    <div
                      className="relative text-6xl lg:text-7xl xl:text-8xl"
                      style={{
                        backgroundImage:
                          'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      13+
                    </div>
                  </div>
                  <p className="text-lg lg:text-2xl xl:text-3xl text-[#9ca3af]">
                    Projects I Have
                    <br />
                    Worked on
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Our Story Section with Blobs */}
        <section className="relative px-4 sm:px-6 lg:px-8 py-12 lg:py-20 overflow-hidden">
          {/* Animated Blob Backgrounds */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <svg
              className="absolute left-[-10%] top-0 w-full h-full opacity-30"
              viewBox="0 0 1280 1099"
              fill="none"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={svgPaths.p1b9dbe00}
                fill="url(#paint0_linear_blobs)"
                className="animate-blob-slow"
              />
              <path
                d={svgPaths.p1390bb70}
                fill="url(#paint1_linear_blobs)"
                className="animate-blob-slow"
                style={{ animationDelay: '2s' }}
              />
              <defs>
                <linearGradient
                  id="paint0_linear_blobs"
                  x1="560.848"
                  y1="447.777"
                  x2="560.848"
                  y2="1042.74"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#6155F5" />
                  <stop offset="1" stopColor="#FF2D55" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_blobs"
                  x1="700.279"
                  y1="-3.61545"
                  x2="700.279"
                  y2="508.434"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#00C3D0" />
                  <stop offset="1" stopColor="#CB30E0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="max-w-[90rem] mx-auto relative z-10 mt-2 lg:mt-4 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-16 lg:gap-24 items-center">
              
              {/* Top: Showcase Collage */}
              <div className="w-full max-w-6xl flex flex-col gap-8 lg:gap-12">
                
                {/* Desktop/Web Showcase Banner */}
                <div className="relative w-full rounded-[36px] overflow-hidden bg-black/40 border-2 border-white/10 hover:border-[#cb30e0]/70 transition-all duration-500 shadow-xl group">
                  <img src="/images/BannerZumify.webp" alt="Zumify Web Design" className="w-full h-auto object-cover transform scale-[1.01] group-hover:scale-105 transition-transform duration-700 ease-out" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
                  <div className="absolute bottom-4 left-6 lg:bottom-6 lg:left-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-y-2 group-hover:translate-y-0">
                    <p className="text-white font-medium text-lg lg:text-xl tracking-wide drop-shadow-md">Zumify is about Relationships and Digital Support</p>
                  </div>
                </div>

                {/* Mobile Showcase Videos */}
                <div className="w-full flex flex-col gap-6 lg:gap-8 px-4 sm:px-0 mt-4 lg:mt-6">
                  <h3 className="text-2xl sm:text-3xl text-white font-medium text-center drop-shadow-md">
                    Effective mobile designs for your business
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10">
                    {[
                      {
                        src: isMobile ? "/videos/HawaiiLoaCareMobile_mob.mp4" : "/videos/HawaiiLoaCareMobile.mp4",
                        webmSrc: isMobile ? "/videos/HawaiiLoaCareMobile_mob.webm" : "/videos/HawaiiLoaCareMobile.webm",
                        imgSrc: "/images/HawaiiLoaCareMobile.webp",
                        title: "Hawaii Loa Care",
                        href: "https://hawaiiloacare.com/"
                      },
                      {
                        src: isMobile ? "/videos/LogynMobile_mob.mp4" : "/videos/LogynMobile.MP4",
                        webmSrc: isMobile ? "/videos/LogynMobile_mob.webm" : "/videos/LogynMobile.webm",
                        imgSrc: "/images/LogynOkudaMobile.webp",
                        title: "Logyn Okuda",
                        href: "https://logynokuda.com/"
                      },
                      {
                        src: isMobile ? "/videos/WaiolaMobile_mob.mp4" : "/videos/WaiolaMobile.mp4",
                        webmSrc: isMobile ? "/videos/WaiolaMobile_mob.webm" : "/videos/WaiolaMobile.webm",
                        imgSrc: "/images/WaiolaShaveIceMobile.webp",
                        title: "Waiola Shave Ice",
                        href: "https://www.waiolashaveice.co/"
                      }
                    ].map((video, idx) => {
                      const content = (
                        <>
                          <p className="text-white/90 text-sm md:text-base font-medium tracking-wide group-hover:text-[#00C3D0] transition-colors text-center w-full">{video.title}</p>
                          <div className="relative w-full max-w-[320px] sm:max-w-none aspect-[9/16] rounded-[36px] overflow-hidden bg-black/40 border-2 border-white/10 hover:border-[#00C3D0]/70 transition-all duration-500 shadow-xl hover:shadow-[0_0_30px_rgba(0,195,208,0.3)]">
                            {showVideo ? (
                              <LazyVideo
                                src={video.src}
                                webmSrc={video.webmSrc}
                                poster={video.imgSrc}
                                className="absolute inset-0 w-full h-full"
                                videoClassName="object-cover transform scale-[1.01] group-hover:scale-105 transition-transform duration-700 ease-out"
                              />
                            ) : (
                              <img
                                src={video.imgSrc}
                                alt={video.title}
                                className="absolute inset-0 w-full h-full object-cover transform scale-[1.01] group-hover:scale-105 transition-transform duration-700 ease-out"
                                loading="lazy"
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
                          </div>
                        </>
                      );
                      
                      return video.href ? (
                        <a key={idx} href={video.href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-3 lg:gap-4 group w-full cursor-pointer">
                          {content}
                        </a>
                      ) : (
                        <div key={idx} className="flex flex-col items-center gap-3 lg:gap-4 group w-full">
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Bottom: Text Block */}
              <div className="w-full max-w-5xl">
                <div className="backdrop-blur-sm bg-gradient-to-b from-[rgba(116,116,128,0.08)] to-black/95 rounded-[40px] lg:rounded-[50px] p-6 sm:p-8 lg:p-14 border-2 border-[#cb30e0] shadow-2xl hover:shadow-[#cb30e0]/10 transition-shadow duration-500">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white text-center mb-8 lg:mb-12 font-medium">
                    Local Run Software and Design Business
                  </h2>
                  <div className="space-y-6 lg:space-y-8 text-white/90 text-lg sm:text-xl lg:text-2xl leading-relaxed">
                    <p>
                      Founded in 2025 as a family-run business, Zumify specializes in building
                      high-quality apps and websites across multiple disciplines. We recognized a major
                      gap in Hawaiʻi's technology landscape—many local systems still feel outdated and
                      poorly presented. After hearing the same frustrations from our community, we set
                      out to bring modern, custom-built digital experiences to Hawaiʻi.
                    </p>
                    <p>
                      We don't aim to be basic; we aim to elevate our clients above their competitors.
                      Our clients become part of our extended family, and their success becomes our
                      priority. Through seamless, integrated digital solutions, we help them streamline
                      operations, strengthen their brand, and invest in their growth. Zumify was built
                      on one belief: transforming the way we design, build, and innovate to uplift the
                      people we serve.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10 mt-16 lg:mt-32">
             <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white text-center mb-6 lg:mb-10">
                More about Wolfgang
              </h2>
            <div className="backdrop-blur-sm bg-gradient-to-b from-[rgba(116,116,128,0.08)] to-black/95 rounded-[40px] lg:rounded-[50px] p-6 sm:p-8 lg:p-12 border-2 border-cyan-500/50 flex flex-col gap-10 lg:gap-16 items-center">
              
              {/* Images Grid (Top) */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-10">
                <motion.div
                  className="relative group rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/20 aspect-[4/3] sm:aspect-video"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src="/images/WaffleDogs.webp"
                    alt="Waffle Dogs"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
                
                <motion.div
                  className="relative group rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/20 aspect-[4/3] sm:aspect-video"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <LazyVideo
                    src={isMobile ? "/videos/WolfgangCoding_mob.mp4" : "/videos/WolfgangCoding.mp4"}
                    webmSrc={isMobile ? "/videos/WolfgangCoding_mob.webm" : "/videos/WolfgangCoding.webm"}
                    poster="/images/workingWebDesign-640w.webp"
                    className="w-full h-full"
                    videoClassName="object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              </div>

              {/* Text Content (Bottom) */}
              <div className="w-full max-w-4xl space-y-6 lg:space-y-8 text-white/90 text-base sm:text-lg lg:text-xl leading-relaxed">
                <p>
                  Wolfgang Sakamaki is a Designer, Developer, and Business Strategist whose passion lies in helping businesses, organizations, and communities thrive through thoughtful digital experiences. As the founder of Zumify, he combines creative design with business strategy to build websites, software interfaces, and digital solutions that are both visually compelling and results-driven.
                </p>
                <p>
                  Wolfgang earned his degree in Business Administration with an emphasis in Management and a minor in Film Advertisement from Chapman University. During his time there, he competed as an NCCAA collegiate athlete while participating in Alpha Kappa Psi, the professional business fraternity. His studies in advertising introduced him to graphic design and visual communication, where he discovered a passion that would ultimately shape his career.
                </p>
                <p>
                  After experiencing life on the mainland, Wolfgang chose to return home to Hawaiʻi, believing that the strength of the islands comes from the relationships built within the community. He founded Zumify with the mission of becoming more than a design agency—to serve as a long-term digital partner that helps local businesses and organizations grow with confidence.
                </p>
                <p>
                  His background in business management allows him to approach every project strategically. Rather than focusing solely on aesthetics, Wolfgang studies each client's industry, competitors, and goals to create digital products that become valuable business investments. His work has helped organizations modernize outdated systems, improve accessibility and user experience, increase online visibility, generate qualified leads, and strengthen their professional brand. His portfolio includes collaborations with law firms, nonprofit organizations, care home facilities, local businesses, and other community organizations throughout Hawaiʻi.
                </p>
                <p>
                  Beyond his professional work, Wolfgang believes success is measured by service to others. He enjoys building lasting relationships with clients through genuine conversations and a commitment to helping others succeed. His philosophy is simple: in a world that often feels busy and divided, small acts of kindness and meaningful partnerships can make a lasting difference.
                </p>
                <p>
                  Outside of Zumify, Wolfgang serves as an Assistant Coach for Cross Country and Track & Field at ʻIolani School. He is also actively involved in several community and business organizations, including the Honolulu Japanese Junior Chamber of Commerce (HJJCC), the Japanese Chamber of Commerce and Industry of Hawaiʻi (JCCH), and his neighborhood's Social and pickleball community. Through both his professional work and community involvement, Wolfgang remains committed to creating opportunities, strengthening relationships, and giving back to the people who have supported his journey.
                </p>
              </div>
            </div>
          </div>

          <style>{`
          @keyframes blob-slow {
            0%, 100% {
              transform: translate(0, 0) scale(1);
              opacity: 0.3;
            }
            33% {
              transform: translate(30px, -30px) scale(1.05);
              opacity: 0.4;
            }
            66% {
              transform: translate(-20px, 20px) scale(0.95);
              opacity: 0.35;
            }
          }
          .animate-blob-slow {
            animation: blob-slow 20s ease-in-out infinite;
          }
        `}</style>
        </section>

        {/* What Drives Us Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-800/60">
          <div className="max-w-7xl mx-auto relative z-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white text-center mb-12">
              How we drive local Hawaiʻi based businesses forward
            </h2>
            <div className="max-w-4xl mx-auto flex flex-col gap-4">
              {[
                {
                  title: 'Innovation',
                  description:
                    "Bringing modern, cutting-edge solutions in and out of Hawaiʻi's digital landscape. We leverage the latest technologies—from immersive 3D experiences to lightning-fast web frameworks—to ensure your business stays ahead of the curve. Innovation isn't just about using new tools; it's about solving old problems in powerful new ways.",
                  IconComponent: Lightbulb,
                },
                {
                  title: 'Excellence',
                  description:
                    'Elevating our clients above their competitors with superior quality. We hold every project to the highest standards of design, performance, and accessibility. Excellence is built into every line of code and every pixel, ensuring your digital presence is robust, visually stunning, and reliable.',
                  IconComponent: Star,
                },
                {
                  title: 'Family',
                  description: 'Treating every client as part of our extended ʻohana. We build deep, lasting relationships based on trust, transparency, and mutual respect. When you work with Zumify, you are not just a client; you are a partner we genuinely care about and support through every step of your digital journey.',
                  IconComponent: Users,
                },
                {
                  title: 'Supporting All Locations',
                  description:
                    'Understanding and serving unique business needs both in and beyond Hawaiʻi. Whether you are a local mom-and-pop shop on Oʻahu or a growing enterprise on the mainland, we tailor our strategies to connect you with your specific target audience and market demands.',
                  IconComponent: MapPin,
                },
                {
                  title: 'Growth',
                  description: 'Helping businesses streamline operations and expand. Our digital solutions are designed to be an investment that yields measurable returns. From generating qualified leads to automating tedious tasks, our goal is to empower your business to scale efficiently and confidently.',
                  IconComponent: TrendingUp,
                },
                {
                  title: 'Partnership',
                  description: "Your success is our priority—we're in this together. We don't just hand off a website and disappear. We stand by you as your long-term digital partner, providing ongoing support, analytics, and strategy to ensure you continue to thrive long after launch.",
                  IconComponent: Handshake,
                },
              ].map((value, i) => {
                 const isOpen = activeAccordion === i;
                 return (
                    <div 
                      key={i} 
                      className={`bg-zinc-900/50 backdrop-blur-sm border-2 ${isOpen ? 'border-[#00C3D0] shadow-[0_0_20px_rgba(0,195,208,0.2)]' : 'border-purple-600/20 hover:border-purple-600/60'} rounded-2xl overflow-hidden transition-all duration-300`}
                    >
                      <button 
                        onClick={() => setActiveAccordion(isOpen ? null : i)}
                        className="w-full flex items-center justify-between p-6 lg:p-8 text-left group outline-none"
                      >
                         <div className="flex items-center gap-4 lg:gap-6">
                            <div className="relative inline-block flex-shrink-0">
                              <value.IconComponent 
                                className={`w-8 h-8 lg:w-10 lg:h-10 transition-colors duration-300 ${isOpen ? 'text-[#00C3D0]' : 'text-cyan-400/70 group-hover:text-cyan-400'}`} 
                                strokeWidth={1.5} 
                              />
                              {isOpen && <div className="absolute inset-0 blur-xl bg-cyan-400/40 transition-all duration-300 -z-10" />}
                            </div>
                            <h3 className={`text-xl lg:text-2xl font-medium transition-colors duration-300 ${isOpen ? 'text-white' : 'text-white/80 group-hover:text-white'}`}>
                              {value.title}
                            </h3>
                         </div>
                         <ChevronDown 
                           className={`w-6 h-6 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#00C3D0]' : 'text-stone-400 group-hover:text-white'}`} 
                         />
                      </button>
                      
                      <div 
                        className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
                      >
                         <div className="px-6 pb-6 pt-0 lg:px-8 lg:pb-8 lg:pl-[5.5rem]">
                            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                              {value.description}
                            </p>
                         </div>
                      </div>
                    </div>
                 );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Want Zumify to help your business?
            </h2>
            <p className="text-lg sm:text-xl text-stone-300 mb-8">
              Let's bring your vision to life with modern, custom-built digital solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => router.push('/contact')}
                className="px-8 py-4 bg-black/60 backdrop-blur-sm border-2 border-purple-600 text-white rounded-full hover:bg-purple-900 transition-all duration-300"
              >
                Get in Touch
              </button>
              <button
                onClick={() => router.push('/projects')}
                className="px-8 py-4 bg-black/60 backdrop-blur-sm border-2 border-purple-600 text-white rounded-full hover:bg-purple-900 transition-all duration-300"
              >
                View Our Work
              </button>
            </div>
          </div>
        </section>

        <ResponsiveFooter />
      </div>
    </div>
  );
}
